<template>
  <ElDialog :title="title" :visible="value" width="30%" @close="resetAndClose">
    <span v-if="isUnverifiedReviewRequest">
      {{ unverifiedReviewRequestDesc }}
    </span>

    <template v-else>
      <span v-if="actionDesc" v-dompurify-html="actionDesc" />

      <AppActionForm
        :action-name="actionName"
        :autopublish.sync="requestModel.autopublish"
        :developer-note.sync="requestModel.developerNote"
      />

      <ErrorsList
        v-if="errors"
        :error-guide="$t('APP.ACTIONS.ERRORS')"
        :errors="errors"
      />

      <div slot="footer" class="dialog-footer">
        <ElButton
          type="primary"
          native-type="submit"
          size="large"
          :loading="loading"
          @click.native.prevent="sendRequest"
        >
          {{ title }}
        </ElButton>

        <ElButton
          type="text"
          :plain="true"
          @click.native.prevent="resetAndClose"
        >
          {{ $t('CANCEL') }}
        </ElButton>
      </div>
    </template>
  </ElDialog>
</template>

<script>
import ErrorsList from '@@/common/ErrorsList';
import AppActionForm from './AppActionForm';

import PublishActions from '@/api/app-publish-actions';
import { APP_DETAILS } from '@/store/types';
import { mapGetters, mapMutations } from 'vuex';
import packageName from '@/mixins/packageName';

export default {
  name: 'AppActionModal',

  components: {
    ErrorsList,
    AppActionForm,
  },

  mixins: [packageName],

  props: {
    title: {
      type: String,
      default: '',
    },
    value: {
      type: Boolean,
      default: false,
    },
    actionName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      requestModel: {
        developerNote: '',
        autopublish: false,
      },
      errors: null,
      loading: false,
    };
  },

  computed: {
    ...mapGetters({ app: APP_DETAILS }),

    isUnverifiedReviewRequest() {
      const reviewRequestActions = [
        'publish_request',
        'warning_removal_request',
        'suspension_removal_request',
      ];
      const isReviewRequest = reviewRequestActions.includes(this.actionName);
      return isReviewRequest && !this.canRequestAppReview;
    },

    canRequestAppReview() {
      const isFreeApp = this.app.price === 0 && !this.app.haveIap;
      const isVerifiedForFree =
        this.verificationState === 'J' || this.verificationState === 'R';
      return this.verificationState === 'A' || (isFreeApp && isVerifiedForFree);
    },

    unverifiedReviewRequestDesc() {
      return this.$t(
        `APP.ACTIONS.UNVERIFIED_ACCOUNT_${this.verificationState}_DESC`,
      );
    },

    verificationState() {
      return this.$auth.user.publisher.verificationState;
    },

    actionDesc() {
      // to-be-removed: Right now, we don't have a different description
      let translationKey;
      if (
        this.actionName === 'warning_removal_request' ||
        this.actionName === 'suspension_removal_request'
      ) {
        translationKey = 'APP.ACTIONS.PUBLISH_REQUEST_DESC';
      } else if (
        this.actionName === 'cancel_warning_removal_request' ||
        this.actionName === 'cancel_suspension_removal_request'
      ) {
        translationKey = 'APP.ACTIONS.CANCEL_PUBLISH_REQUEST_DESC';
      } else {
        translationKey = `APP.ACTIONS.${this.actionName.toUpperCase()}_DESC`;
      }

      return this.$t(translationKey);
    },
  },

  methods: {
    ...mapMutations({ commitAppDetails: APP_DETAILS }),

    sendRequest() {
      this.loading = true;
      return PublishActions.submitAction({
        packageName: this.packageName,
        action: this.actionName,
        data: this.requestModel,
      })
        .then(({ publish_info, message }) => {
          if (this.actionName === 'hide') {
            this.$router.replace({ name: 'apps' });
          }

          this.commitAppDetails({ ...this.app, ...publish_info });
          this.resetRequestModel();
          this.$message.success(message);
          this.resetAndClose();
        })
        .catch(({ message, errors }) => {
          if (message) {
            this.$message.error(message);
          }

          this.errors = errors;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    resetAndClose() {
      this.errors = null;
      this.$emit('input', false);
    },

    resetRequestModel() {
      this.requestModel = {
        developerNote: '',
        autopublish: false,
      };
    },
  },
};
</script>
